<script lang="ts" setup>
/**
 * LA PASTILLE D'ORIGINE (DEV-1508 lot 4, item 8 du brief de design v1.1).
 *
 * INTENTION : sur cette page, une couleur veut dire UNE chose. Avant ce
 * composant, le graphe et le tableau tenaient chacun leur jeu de classes, et
 * « Mesuré » s'affichait en marine dans le graphe — la couleur du primaire, des
 * liens et de l'onglet actif — pendant que la bande de complétude, à trente
 * pixels au-dessus, disait « Mesuré » en vert. Deux couleurs pour le même mot,
 * sur le même écran.
 *
 * Les couleurs viennent donc du référentiel de qualité de l'application
 * (`data/quality/dataQuality.ts`), et chaque couple porte SON RATIO DE CONTRASTE
 * MESURÉ en commentaire : un contraste ne se suppose pas (charte, art. 3).
 *
 * Le cas « absente » n'est pas une origine : c'est l'absence d'origine. Il se
 * lit comme un contenant non rempli — pas de fond, un trait pointillé — plutôt
 * que comme une cinquième provenance. Un fond teinté l'aurait rapproché du gris
 * de « Corrigé », et le couple gris sur gris clair d'abord envisagé ne mesurait
 * que 4,34:1, sous le seuil AA pour du 12 px.
 *
 * Les classes `graphe-enedis__etiquette--*` sont conservées comme CROCHETS DE
 * TEST (`SourceDataEnedisView.miseEnForme.spec.ts` ligne 163), pas comme
 * porteuses de style : le style est posé en ligne, par la table ci-dessous.
 */
import type { OrigineSerie } from "@modules/data/utils/ongletsEnedis"

/** Les quatre origines, plus l'absence d'origine, qui n'en est pas une. */
export type OriginePastille = OrigineSerie | "absente"

type StylePastille = {
    color: string
    backgroundColor: string
    border: string
}

/**
 * La table des couleurs, en hexadécimal et non en `var()` : le préréglage de
 * l'application redéfinit `blue.300` et `blue.700`, et une pastille qui lit ces
 * jetons change de sens d'un contexte à l'autre.
 */
const STYLES: Record<OriginePastille, StylePastille> = {
    // QUALITY[0], le vert de la bande de complétude — 4,57:1
    mesure: {
        color: "#15803d",
        backgroundColor: "#dcfce7",
        border: "1px solid transparent",
    },
    // QUALITY[2], le bleu informatif — 5,49:1
    calcul: {
        color: "#1d4ed8",
        backgroundColor: "#dbeafe",
        border: "1px solid transparent",
    },
    // QUALITY[1], l'ambre de prudence — 4,51:1. Le tiret est conservé : une
    // estimation n'est pas un calcul certain, et cela se voit sans la couleur.
    estimation: {
        color: "#b45309",
        backgroundColor: "#fef3c7",
        border: "1px dashed #f59e0b",
    },
    // QUALITY[3], le gris — 6,92:1
    correction: {
        color: "#475569",
        backgroundColor: "#f1f5f9",
        border: "1px solid transparent",
    },
    // Pas une origine : un contenant vide — 10,35:1 sur le fond blanc de la carte
    absente: {
        color: "#334155",
        backgroundColor: "transparent",
        border: "1px dashed #94a3b8",
    },
}

const props = defineProps<{
    origine: OriginePastille
    /** Le mot tel qu'il se lit : « Mesuré », « Mesuré 50 % », « Non publié ». */
    libelle: string
}>()

const style = STYLES[props.origine]
</script>

<template>
    <span
        class="graphe-enedis__etiquette inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
        :class="[
            `graphe-enedis__etiquette--${origine}`,
            origine === 'absente' ? 'font-semibold' : '',
        ]"
        :data-origine="origine"
        :style="style"
        >{{ libelle }}</span
    >
</template>
